/**
 * Eigene Krankheitstage und Krankheitsausfall in der Oberfläche: Grenzen und
 * Beschriftung an einer Stelle (Spezifikation 2026-09-16, R5).
 *
 * Grenzen nach Messung und externen Vergleichswerten (Marie 18.09.2026: „Ja, setz
 * um"; ersetzt die Umrechnung × 5 ÷ 7 vom 16.09.). Ein Signal soll etwa die
 * auffälligsten 10 % zeigen, nicht jeden Dritten: Bei 359 Mitarbeitern mit
 * mindestens 12 Monaten liegen die eigenen Krankheitstage im Median bei 7, im
 * oberen Viertel über 14, im oberen Zehntel über 24 Arbeitstagen; die
 * Krankmeldungen (zusammenhängende Einträge einmal gezählt, alle 437
 * Mitarbeiter) im Median bei 3, im oberen Zehntel über 8. Bundesweit 14,6
 * Arbeitstage (Statistisches Bundesamt 2025, nur Meldungen über 3 Tage).
 * Dieselben Grenzen gelten für die Bindungs-Signale in
 * `src/services/retention-scoring.ts`.
 */

/** Rot = Signal, orange = Beobachtung, null = unauffällig. */
export type Stufe = "rot" | "orange" | null;

/** Jeweils sobald der Wert die Grenze ÜBERSCHREITET (>). */
export const SCHWELLEN_EIGENE_KRANKHEITSTAGE = {
  /** Arbeitstage in 12 Monaten: rot über 20 (klar über dem Bundesschnitt, vor der BEM-Grenze von 30), orange über 14 (etwa Bundesschnitt) */
  tage12m: { rot: 20, orange: 14 },
  /** Arbeitstage in 3 Monaten: rot über 5 (ein Viertel der 20) */
  tage3m: { rot: 5, orange: null },
  /** Krankmeldungen in 12 Monaten, zusammenhängende Einträge einmal gezählt: rot über 8 (etwa die auffälligsten 10 %), orange über 5 (etwa das obere Fünftel) */
  meldungen12m: { rot: 8, orange: 5 },
} as const;

/** Onboarding-Gesundheit: Abzug nach eigenen Krankheitstagen in 3 Monaten (wie vor Plan B). */
export const ONBOARDING_GESUNDHEIT_KRANKHEIT = {
  /** −15 Punkte ab mehr als 5 Arbeitstagen — dieselbe Grenze wie das Signal */
  minus15Ab: 5,
  /** −5 Punkte ab mehr als 3 Arbeitstagen */
  minus5Ab: 3,
} as const;

/** Personalleiter-Cockpit: Person wie im Mitarbeiterfenster, Team-Schnitt mit eigenen Grenzen. */
export const COCKPIT_EIGENE_KRANKHEITSTAGE = {
  personRot: SCHWELLEN_EIGENE_KRANKHEITSTAGE.tage12m.rot,
  personOrange: SCHWELLEN_EIGENE_KRANKHEITSTAGE.tage12m.orange,
  /** Team-Schnitt über dem Bundesschnitt (14,6 Arbeitstage) */
  teamRot: 15,
  /** Team-Schnitt über dem mindsquare-Schnitt (11 Arbeitstage) */
  teamOrange: 11,
} as const;

/** Stufe eines Werts: „rot" über `rot`, „orange" über `orange`, sonst null. */
export function krankheitsStufe(
  wert: number,
  grenze: { rot: number; orange: number | null },
): Stufe {
  if (wert > grenze.rot) return "rot";
  if (grenze.orange !== null && wert > grenze.orange) return "orange";
  return null;
}

/** „1 Arbeitstag", „2,5 Arbeitstage", „0 Arbeitstage". */
export function arbeitstageText(tage: number): string {
  return `${tage.toLocaleString("de-DE")} ${tage === 1 ? "Arbeitstag" : "Arbeitstage"}`;
}

/** Punktabzug der Onboarding-Gesundheit für eigene Krankheitstage in 3 Monaten. */
export function onboardingAbzugKrankheit(tage3m: number): number {
  if (tage3m > ONBOARDING_GESUNDHEIT_KRANKHEIT.minus15Ab) return 15;
  if (tage3m > ONBOARDING_GESUNDHEIT_KRANKHEIT.minus5Ab) return 5;
  return 0;
}
